/* eslint-disable class-methods-use-this */
import { Ability } from '@casl/ability';
import { createCanBoundTo } from '@casl/react';
import axios from 'axios';
import dayjs from 'dayjs';
import jwt_decode from 'jwt-decode';
import { object, string, number, boolean } from 'yup';

import apolloClient from '../../services/App/Client';
import appRouter from '../../services/AppRouter/AppRouter';
import userRequests from '../../services/users/gqlSchema';

const LEADER_VALUES = ['LEADER', 'OPTEAM_LEADER', 'OPTEAM_LEADER_ACCREDITE'];

class Auth {
  constructor() {
    this.resetInternalVariables();

    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
  }

  resetInternalVariables() {
    this._isAuthenticated = false;
    this.user = null;
    this.token = null;
    this.decodedToken = null;
    this.refreshToken = null;
    this.decodedRefreshToken = null;
    this.canComponent = null;
    this.ability = null;
  }

  /**
   * Return true if a user is currently connected
   * @returns {boolean} User is authenticated
   */
  isAuthenticated() {
    return this._isAuthenticated;
  }

  /**
   * Return true if current user is an impersonating
   * @returns {boolean} User is impersonating
   */
  isImpersonating() {
    return !!this.decodedToken.options && 'impersonatorId' in this.decodedToken.options;
  }

  saveToken({ accessToken, refreshToken }) {
    this.token = accessToken;
    this.decodedToken = jwt_decode(this.token);

    if (refreshToken) {
      this.refreshToken = refreshToken;
      this.decodedRefreshToken = jwt_decode(this.refreshToken);
    }

    this.setSession({
      access_token: accessToken,
      expires_at: this.decodedToken.exp,
      ...(refreshToken && { refresh_token: refreshToken, refresh_token_expires_at: this.decodedRefreshToken.exp }),
    });
  }

  /**
   * Handle Authentication from localstorage
   * @param {boolean} logoutIfNoUserFound If true, perform an auto logout
   * @param {?string} token If true, perform an auto logout
   * @returns {Promise<boolean>} If something went wrong during the authentication process
   */
  async handleAuthentication({ logoutIfNoUserFound = true, accessToken = null, refreshToken = null }) {
    if (accessToken) {
      this.saveToken({ accessToken, refreshToken });
    }

    // Prevent from logout because we need to check refreshtoken validity aswell
    if (!this.validateTokenFromLocalstorage({ logoutIfNoUserFound: false })) {
      if (!this.validateRefreshTokenFromLocalstorage({ logoutIfNoUserFound })) {
        return false;
      }

      // We can refresh the token
      this.refreshToken = localStorage.getItem('refresh_token');
      const newAccessToken = await this.tokenRefresh();

      if (!newAccessToken) return false;

      this.saveToken({ accessToken: newAccessToken, refreshToken: this.refreshToken });
    }

    // When restoring authentication from localstorage
    if (!this.getToken()) {
      this.saveToken({
        accessToken: localStorage.getItem('access_token'),
        refreshToken: localStorage.getItem('refresh_token'),
      });
    }

    this._isAuthenticated = await this.fetchUserFromToken();

    if (!this._isAuthenticated) {
      console.warn('Auth Service - No user found, need reconnection.');
      this.logout({ redirectToLogin: logoutIfNoUserFound });
    }

    return this.isAuthenticated();
  }

  validateTokenFromLocalstorage({ logoutIfNoUserFound = true }) {
    if (!(localStorage.getItem('access_token') && localStorage.getItem('expires_at'))) {
      if (logoutIfNoUserFound) {
        this.logout({ redirectToLogin: logoutIfNoUserFound });
      }

      return false;
    }

    if (localStorage.getItem('expires_at') < dayjs().unix()) {
      if (logoutIfNoUserFound) {
        this.logout({
          redirectToLogin: logoutIfNoUserFound,
          redirectMessage: 'Votre session a expiré, merci de vous reconnecter.',
        });
      }

      return false;
    }

    return true;
  }
  validateRefreshTokenFromLocalstorage({ logoutIfNoUserFound = true }) {
    if (!(localStorage.getItem('refresh_token') && localStorage.getItem('refresh_token_expires_at'))) {
      if (logoutIfNoUserFound) {
        this.logout({ redirectToLogin: logoutIfNoUserFound });
      }

      return false;
    }

    if (localStorage.getItem('refresh_token_expires_at') < dayjs().unix()) {
      if (logoutIfNoUserFound) {
        this.logout({
          redirectToLogin: logoutIfNoUserFound,
          redirectMessage: 'Votre session a expiré, merci de vous reconnecter.',
        });
      }

      return false;
    }

    return true;
  }

  canRefreshToken() {
    return this.decodedRefreshToken?.exp ? this.decodedRefreshToken.exp >= dayjs().unix() : false;
  }

  async tokenRefresh() {
    const result = await axios.get(`${process.env.REACT_APP_AUTH_BASE_ENDPOINT}/refresh-token`, {
      headers: {
        Authorization: `Bearer ${this.refreshToken}`,
      },
    });

    if (result.data.success) {
      return result.data.accessToken;
    }

    throw Error('Refresh token expired');
  }

  async fetchUserFromToken() {
    if (!this.token || !this.decodedToken || !this.decodedToken.user || !this.decodedToken.user.id) {
      console.warn('User unfetchable. No token or no user found in token.');

      return false;
    }

    const request = await apolloClient.query({
      query: userRequests.Query.me,
    });

    if (!request || !request.data || !request.data.me) {
      console.warn('User unfetchable. API failed to request user infos.');

      return false;
    }

    if (this.decodedToken.options && 'impersonatorId' in this.decodedToken.options) {
      await apolloClient.mutate({
        mutation: userRequests.Mutation.logImpersonationUsage,
        variables: {
          userId: this.decodedToken.user.id,
          impersonatorId: this.decodedToken.options.impersonatorId,
        },
      });
    }

    this.user = request.data.me;
    this.ability = new Ability(this.getUserPermissionRules() || []);
    this.canComponent = createCanBoundTo(this.ability);

    return true;
  }

  /**
   * Save the current session in the local storage
   * The session represent a token
   * @param {Object} authResult accessToken and exp / refreshToken and exp
   * @returns {void} Nothing
   */
  setSession(authResult) {
    localStorage.setItem('access_token', authResult.access_token);
    localStorage.setItem('expires_at', authResult.expires_at);
    localStorage.setItem('refresh_token', authResult.refresh_token);
    localStorage.setItem('refresh_token_expires_at', authResult.refresh_token_expires_at);
  }

  /**
   * Perform a logout by removing the session and optionally redirect to the login page
   * @param {boolean} redirectToLogin Redirection to login
   * @param {string} redirectMessage Message when redirect
   * @returns {void} Nothing
   */
  logout({ redirectToLogin = true, redirectMessage = '' } = {}) {
    // Clear access token from local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('refresh_token_expires_at');

    this.resetInternalVariables();

    if (redirectToLogin) {
      this.hardRedirectToLogin(redirectMessage);
    }
  }

  /**
   * Set the current user info from the given token
   * @param {String} token Token to handle
   * @returns {void} Nothing
   */
  setUserInfoFromToken(token) {
    const decodedToken = jwt_decode(token);

    this.user = decodedToken.user;
    this.token = token;
    this.ability = new Ability(this.getUserPermissionRules() || []);

    this.canComponent = createCanBoundTo(this.ability);
  }

  /**
   * Return the current logged user
   * @return {object} the current logged user
   */
  getUser() {
    return this.user;
  }

  /**
   * Return the current logged contact
   * @return {object} the current logged contact
   */
  getContact() {
    const user = this.getUser();

    return user && user.contact;
  }

  _filterContactInfoUserSection({ sources }) {
    return ['USER', 'USERS'].includes(sources?.section);
  }
  /**
   * Return the current logged contact info
   * @return {Object} the current logged contact info
   */
  getContactInfo() {
    if (!(this.user && this.user.contact && this.user.contact.contactsInfos && this.user.contact.contactsInfos[0])) {
      return null;
    }

    const contactInfos = this.findContactsInfos({
      sources: {
        hasExternalId: true,
        active: true,
        source: 'TREMPLIN',
      },
    }).filter(this._filterContactInfoUserSection);

    if (contactInfos.length > 0) {
      return contactInfos[0];
    }

    const validContactInfosUserSource = this.user.contact.contactsInfos.filter(this._filterContactInfoUserSection);
    if (validContactInfosUserSource.length === 0) {
      console.error(
        `getContactInfo - contactId ${this.user.contact.id} error contactsInfos with valid section not found`,
      );

      return null;
    }

    return validContactInfosUserSource[0];
  }

  getContactsInfos() {
    const contact = this.getContact();

    return contact && contact.contactsInfos;
  }

  getAccessLeader() {
    return this.getUser().organizationRoleCode.some(arr => LEADER_VALUES.includes(arr));
  }

  getRoleLeader() {
    let role = 'AGENT';
    const ROLES_LEAD_OH = ['OPTEAM_LEADER_ACCREDITE', 'OPTEAM_LEADER', 'RESPONSABLE_1'];
    const ROLES_LEAD_CAPI = ['COACH', 'TEAM_LEADER', 'LEADER_ACCREDITE', 'LEADER'];
    const roles = this.getUser().organizationRoleCode;
    const userBU = this.getContactInfo()?.company || this.findUserCompany();
    switch (userBU.id) {
      case 2: {
        let roleWeightOh = ROLES_LEAD_OH.filter(code => roles.includes(code))[0];
        if (roleWeightOh) role = roleWeightOh;
        break;
      }
      case 3: {
        let roleWeightCapi = ROLES_LEAD_CAPI.filter(code => roles.includes(code))[0];
        if (roleWeightCapi) role = roleWeightCapi;
        break;
      }
      default: {
        role = 'AGENT';
      }
    }

    return role;
  }

  findContactsInfos(filters = {}) {
    const filtersSchema = object({
      sources: object({
        source: string(),
        referer: string(),
        section: string(),
        active: boolean(),
        hasExternalId: boolean(),
      }),
      company: object({
        id: number(),
        hasCompany: boolean(),
      }),
    });

    let validatedFilters = filtersSchema.validateSync(filters);
    if (validatedFilters.error) {
      console.error('Error while validating findContactsInfos() filters', validatedFilters.error);
      validatedFilters = {};
    }

    const contactsInfos = this.getContactsInfos();

    if (contactsInfos === null || !Array.isArray(contactsInfos)) {
      return null;
    }

    let contactsToReturn = contactsInfos;

    if (validatedFilters.sources) {
      if (contactsToReturn && validatedFilters.sources.source) {
        contactsToReturn = contactsToReturn.filter(
          contactInfo => contactInfo.sources && contactInfo.sources.source.includes(validatedFilters.sources.source),
        );
      }
      if (contactsToReturn && validatedFilters.sources.referer) {
        contactsToReturn = contactsToReturn.filter(
          contactInfo => contactInfo.sources && contactInfo.sources.referer === validatedFilters.sources.referer,
        );
      }
      if (contactsToReturn && validatedFilters.sources.section) {
        contactsToReturn = contactsToReturn.filter(
          contactInfo => contactInfo.sources && contactInfo.sources.section === validatedFilters.sources.section,
        );
      }
      if (contactsToReturn && typeof validatedFilters.sources.hasExternalId === 'boolean') {
        contactsToReturn = contactsToReturn.filter(
          contactInfo =>
            contactInfo.sources && !!contactInfo.sources.externalId === validatedFilters.sources.hasExternalId,
        );
      }
      if (contactsToReturn && typeof validatedFilters.sources.active === 'boolean') {
        contactsToReturn = contactsToReturn.filter(
          contactInfo => contactInfo.sources && contactInfo.sources.actif === validatedFilters.sources.active,
        );
      }
    }

    if (validatedFilters.company) {
      if (contactsToReturn && typeof validatedFilters.company.hasCompany === 'boolean') {
        contactsToReturn = contactsToReturn.filter(
          contactInfo => contactInfo.company && !!contactInfo.company.id === validatedFilters.company.hasCompany,
        );
      }
      if (contactsToReturn && typeof validatedFilters.company.id === 'number') {
        contactsToReturn = contactsToReturn.filter(
          contactInfo => contactInfo.company && contactInfo.company.id === validatedFilters.company.id,
        );
      }
    }

    return contactsToReturn;
  }

  findUserCompany() {
    let userContactsInfos = this.getContact()?.isAgent
      ? this.findContactsInfos({
          sources: {
            active: true,
          },
          company: {
            hasCompany: true,
          },
        })
      : this.findContactsInfos({
          company: {
            hasCompany: true,
          },
        });

    if (userContactsInfos && userContactsInfos[0]) {
      userContactsInfos = userContactsInfos.filter(this._filterContactInfoUserSection);
    }

    return userContactsInfos && userContactsInfos[0] && userContactsInfos[0].company;
  }

  /**
   * Return the current authentication token (needed to perform API calls)
   * @return {object} the current authentication token
   */
  getToken() {
    return this.token;
  }

  /**
   * Get a <Can> component to handle user abilities in JSX
   * @return {Object} A <Can> component
   */
  getCanComponent() {
    return this.canComponent;
  }

  /**
   * Check the user ability depending on the passed context
   * @param {string} action Action
   * @param {string} subject Subject
   * @param {string} [field] Field
   * @return {boolean} True if the currently logged user can do the action on the subject...
   */
  can(action, subject, field) {
    if (!this.ability || !this.ability.can) {
      console.warn('Ability not yet initialized');

      return false;
    }

    return this.ability.can(action, subject, field);
  }

  /**
   * Handle received token
   * @param {string} accessToken AccessToken to handle
   * @returns {Promise<boolean>} Is authenticated
   */
  async handleToken({ accessToken, refreshToken }) {
    if (!accessToken) {
      console.warn('No accessToken provided to handleToken');

      return false;
    }

    // Logout before new connection
    this.logout({ redirectToLogin: false });

    await this.handleAuthentication({
      accessToken,
      refreshToken,
      logoutIfNoUserFound: true,
    });

    return this.isAuthenticated();
  }

  setContact(userContact) {
    if (userContact) {
      this.user.contact = userContact;

      return true;
    }

    return false;
  }

  /**
   * Perform a hard URL redirection to the login page with an error message
   * @param {string} message Error message to display on the login page
   * @returns {void} Nothing
   */
  hardRedirectToLogin(message) {
    if (!message) {
      window.location.assign('/#/login/');
    } else {
      window.location.assign('/#/login/failed/' + encodeURIComponent(message));
    }
  }

  /**
   * Reloads the page from the current URL and force the page to always be reloaded from the server.
   * @returns {void} Nothing
   */
  forceReload() {
    window.location.reload(true);
  }

  /**
   * Get the currently logged user permission rules CASL formated
   * @return {Array} permission rules CASL formated
   */
  getUserPermissionRules() {
    let abilities = [];
    const user = this.getUser();

    if (user && user.combinedPermissionRules) {
      abilities = user.combinedPermissionRules.map(rule => ({
        subject: rule.permissionRuleSubjects.map(permissionRuleSubject => permissionRuleSubject.code),
        action: rule.permissionRuleActions.map(permissionRuleAction => permissionRuleAction.code),
      }));
    }

    return abilities;
  }

  redirectToHome() {
    appRouter.goTo(this.getUserHome());
  }

  getUserHome() {
    const contact = this.getContact();
    if (contact && contact.isCandidate) {
      return '/candidate';
    } else {
      return '/';
    }
  }

  storeNextUrl(nextUrl) {
    localStorage.setItem('next_url', nextUrl);
  }

  resetNextUrl() {
    localStorage.removeItem('next_url');
  }

  getNextUrl() {
    return localStorage.getItem('next_url');
  }

  hasNextUrl() {
    return !!this.getNextUrl();
  }

  redirectToNextUrl() {
    if (this.hasNextUrl()) {
      console.log('redirectToNextUrl');
      const nextUrl = this.getNextUrl();
      this.resetNextUrl();
      appRouter.goTo(nextUrl);
    } else {
      console.log('redirectToNextUrl - nop');
    }
  }

  redirectAfterAuthentication() {
    console.log('redirectAfterAuthentication');
    if (this.hasNextUrl()) {
      this.redirectToNextUrl();
    } else {
      console.log('redirectToHome');
      this.redirectToHome();
    }
  }

  /**
   * Get eventual redirect path included in token
   * @returns {string|null} redirect path included in token or undefined
   * @memberOf Auth
   */
  getRedirectPathInToken() {
    return this.decodedToken?.options?.redirect;
  }

  /**
   * Whether a redirect path is included in token
   * @returns {boolean} Whether a redirect path is included in token
   * @memberOf Auth
   */
  hasRedirectPathInToken() {
    return !!this.getRedirectPathInToken();
  }

  /**
   * Check if current user contract is multi
   * @returns {boolean} The current contract is multi
   * @memberOf Auth
   */
  hasMultiFormula() {
    const contracts = this.getContactInfo()?.contractsPeriods || [];

    return contracts
      .filter(contract => contract.actif)
      .some(contract => contract.package && contract.package.name === 'MLM');
  }
}

export default Auth;
